import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function CreateAgent() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    context_text: 'You are a helpful AI assistant.',
    escalation_text: 'For urgent matters, please contact support.',
    response_style: 'normal',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/agents', formData);
      toast.success('Agent created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground" data-testid="create-agent-title">Create New Agent</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-border shadow-lg" data-testid="create-agent-form">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Agent Details</CardTitle>
            <CardDescription>Configure your new AI chat agent</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">Agent Name *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g., Customer Support Agent"
                  value={formData.name}
                  onChange={handleChange}
                  className="h-11 rounded-xl"
                  required
                  data-testid="agent-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="context_text" className="text-sm font-medium">Context / System Prompt *</Label>
                <Textarea
                  id="context_text"
                  name="context_text"
                  placeholder="Describe the agent's role and behavior..."
                  value={formData.context_text}
                  onChange={handleChange}
                  className="min-h-32 rounded-xl"
                  required
                  data-testid="context-text-input"
                />
                <p className="text-xs text-muted-foreground">This defines how the agent behaves and responds</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="escalation_text" className="text-sm font-medium">Escalation Text</Label>
                <Textarea
                  id="escalation_text"
                  name="escalation_text"
                  placeholder="Instructions for escalating to human support..."
                  value={formData.escalation_text}
                  onChange={handleChange}
                  className="min-h-24 rounded-xl"
                  data-testid="escalation-text-input"
                />
                <p className="text-xs text-muted-foreground">Shown when users need to contact support</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="response_style" className="text-sm font-medium">Response Style *</Label>
                <Select
                  value={formData.response_style}
                  onValueChange={(value) => setFormData({ ...formData, response_style: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="response-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short" data-testid="style-short">Short (Brief responses)</SelectItem>
                    <SelectItem value="normal" data-testid="style-normal">Normal (Detailed responses)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 rounded-full"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Creating...' : 'Create Agent'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
